import { Severity } from './queries';

/**
 * Every date on these two lists is epoch SECONDS, and an absent one arrives as
 * null (not 0 — the backend nulls out non-numeric and zero dates rather than
 * letting them through as the partner portal's `!== 0` guards have to).
 */
export const fmtEpoch = (seconds: number | null | undefined): string => {
  if (!seconds) return 'N/A';
  const d = new Date(seconds * 1000);
  if (Number.isNaN(d.getTime())) return 'N/A';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Severity is computed server-side (services/certExpiry.ts) so the list, the
 * filters and the badges can never disagree — the partner portal derives it in
 * two different components and they have already drifted.
 */
export const severityColor = (severity: Severity | null | undefined): string => {
  switch (severity) {
    case 'expired': return '#7b1fa2';
    case 'critical': return '#d32f2f';
    case 'warning': return '#ed6c02';
    default: return '#2e7d32';
  }
};

export const severityLabel = (severity: Severity | null | undefined): string => {
  switch (severity) {
    case 'expired': return 'Expired';
    case 'critical': return 'Critical';
    case 'warning': return 'Warning';
    default: return 'Normal';
  }
};

/**
 * "just now" / "14 min ago" / "3 h ago" / a date once it is a day old.
 *
 * For the refresh status only, whose timestamps are ISO 8601 strings rather
 * than the epoch seconds everything else on this page carries. The point of it
 * being relative is that "2 h ago" answers "is what I am looking at current?",
 * which an absolute clock time makes the reader work out for themselves.
 */
export const fmtAgo = (iso: string | null | undefined): string => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} h ago`;
  return fmtEpoch(Math.floor(then / 1000));
};

/** "12 days" / "lapsed 4 days ago" — days come back negative once lapsed. */
export const fmtDays = (days: number | null | undefined): string => {
  if (days === null || days === undefined) return 'N/A';
  if (days < 0) return `${Math.abs(days)}d ago`;
  return `${days}d`;
};
